import {useCallback, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useIntl, type IntlShape} from 'react-intl';
import {useFetchMsisdnSettingsInfoQuery} from '../../../store/features/MsisdnSettings/msisdnSettingsService';
import {getSelectedMicrophone, getSelectedSpeaker} from '../../../store/features/calls/dialerSlice';
import {CallBubble} from '../components/CallBubble';
import {CallDialog} from '../components/CallDialog';
import {CallKeypad} from '../components/CallKeypad';
import {CallSurfaceBoundary} from '../components/CallSurfaceBoundary';
import {HeldCallRow, type HeldCall} from '../components/HeldCallRow';
import {DevicesDialog} from '../group/GroupDialogs';
import {DevicesPopover} from '../components/DevicesPopover';
import {regionWithState} from '../components/CallStateNotes';
import {matrixSurfaceMessages as dm} from '../matrix/matrixMessages';
import type {PhoneCallLeg} from '../model/types';
import {selectCallSurfaceState, setExpanded, setPanel} from '../store/callSurfaceSlice';
import {PhoneActiveBubble, PhoneCallingBubble, PhoneIncomingBubble, PhoneKeypadBubble, PhoneOutputBubble} from './PhoneBubbleViews';
import {phoneTitleOf} from './phoneText';
import {PhoneStage} from './PhoneStage';
import {usePhoneActions} from './usePhoneActions';
import {usePhoneControls} from './usePhoneControls';
import {phoneMessages as m} from './phoneMessages';

const useLineRecording = (leg: PhoneCallLeg): boolean | undefined => {
    const msisdnId = leg.line?.msisdnId;
    const {data} = useFetchMsisdnSettingsInfoQuery(String(msisdnId ?? ''), {skip: msisdnId === undefined || leg.phase === 'incoming'});
    return data?.callRecordingEnabled;
};

const incomingRegionOf = (leg: PhoneCallLeg, name: string, intl: IntlShape): string =>
    leg.identity.name
        ? intl.formatMessage(m.incomingRegion, {name})
        : intl.formatMessage(leg.likelySpam ? m.incomingSpamRegion : m.incomingUnknownRegion);

export const PhoneCallSurface = ({leg, held}: {leg: PhoneCallLeg; held?: HeldCall}) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const actions = usePhoneActions();
    const recording = useLineRecording(leg);
    const {expanded, panel} = useSelector(selectCallSurfaceState);
    const [devicesOpen, setDevicesOpen] = useState(false);
    const [outputOpen, setOutputOpen] = useState(false);
    const bubbleRef = useRef<HTMLElement>(null);
    const microphone = useSelector(getSelectedMicrophone);
    const speaker = useSelector(getSelectedSpeaker);
    const onStage = expanded && leg.phase !== 'incoming';
    const name = phoneTitleOf(leg);

    const closeOutput = useCallback(() => setOutputOpen(false), []);
    const backToCall = () => dispatch(setPanel('none'));
    const expand = () => dispatch(setExpanded(true));

    const controls = usePhoneControls(leg, actions, {
        iconSize: onStage ? 24 : 20,
        speakerOpen: onStage ? outputOpen : panel === 'output',
        onKeypad: () => dispatch(setPanel('keypad')),
        onSpeaker: () => (onStage ? setOutputOpen(open => !open) : dispatch(setPanel('output'))),
        onDevices: () => setDevicesOpen(open => !open),
        devicesOpen,
    });

    if (leg.phase === 'incoming') {
        return (
            <CallSurfaceBoundary resetKey={leg.id}>
                <CallBubble tone="call" urgent label={incomingRegionOf(leg, name, intl)} testId="phone-call-ring-bubble">
                    <PhoneIncomingBubble leg={leg} actions={actions}/>
                </CallBubble>
            </CallSurfaceBoundary>
        );
    }

    const bubble = leg.phase === 'calling'
        ? <PhoneCallingBubble leg={leg} actions={actions} recording={!!recording} onExpand={expand}/>
        : panel === 'keypad'
            ? <PhoneKeypadBubble actions={actions} onBack={backToCall}/>
            : panel === 'output'
                ? <PhoneOutputBubble actions={actions} onBack={backToCall}/>
                : <PhoneActiveBubble leg={leg} actions={actions} controls={controls} recording={!!recording} onExpand={expand}/>;

    return (
        <CallSurfaceBoundary resetKey={leg.id}>
            {onStage ? (
                <PhoneStage
                    leg={leg}
                    actions={actions}
                    controls={controls}
                    recording={recording}
                    outputOpen={outputOpen}
                    onCloseOutput={closeOutput}
                    onMinimize={() => dispatch(setExpanded(false))}
                />
            ) : (
                <CallBubble ref={bubbleRef} tone="call" label={regionWithState(intl, intl.formatMessage(recording ? m.callRegionRecording : m.callRegion, {name}), {network: leg.network, muted: leg.muted})} testId="phone-call-bubble">
                    {held ? <div className="flex flex-col gap-3"><HeldCallRow {...held}/>{bubble}</div> : bubble}
                </CallBubble>
            )}
            {onStage && held && (
                <div className="phq-call-ui fixed left-1/2 top-4 z-[56] -translate-x-1/2">
                    <HeldCallRow {...held} floating/>
                </div>
            )}
            {onStage && panel === 'keypad' && (
                <CallDialog title={intl.formatMessage(dm.keypadTitle)} closeLabel={intl.formatMessage(dm.backToCall)} width="sm" onClose={backToCall}>
                    <CallKeypad onDigit={actions.dtmf}/>
                </CallDialog>
            )}
            {devicesOpen && !onStage && (
                <DevicesPopover
                    bubble={bubbleRef}
                    lineCall
                    current={{audioInputId: microphone || undefined, audioOutputId: speaker || undefined}}
                    onApply={(kind, deviceId) => {
                        if (kind === 'audioinput') {
                            actions.setMicrophone(deviceId);
                        } else if (kind === 'audiooutput') {
                            actions.setOutput(deviceId);
                        }
                    }}
                    onClose={() => setDevicesOpen(false)}
                />
            )}
            {devicesOpen && onStage && (
                <DevicesDialog
                    showCamera={false}
                    current={{audioInputId: microphone || undefined, audioOutputId: speaker || undefined}}
                    onApply={(kind, deviceId) => {
                        if (kind === 'audioinput') {
                            actions.setMicrophone(deviceId);
                        } else if (kind === 'audiooutput') {
                            actions.setOutput(deviceId);
                        }
                    }}
                    onClose={() => setDevicesOpen(false)}
                />
            )}
        </CallSurfaceBoundary>
    );
};
